import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { FieldArrayType, FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { map, take } from 'rxjs/operators';
import { FileSaverService } from 'ngx-filesaver';
import { DataUrl, DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { ApplicationSettings, ButtonType, CoreService, DataService, DialogResult, DialogService, FIELD_TYPE, FileService, FileUpload, SpinnerService } from 'dspx-core';
import { markChildControlsAsTouched } from './form.shared';
import { OPTION_SETS } from '../../constants/app-data.constants';
import { DateUtilService } from '../../services/date-util.service';
import { HttpClient } from '@angular/common/http';
import { AppDataService } from 'src/app/services/app-data.service';

export interface FileViewEvent {
  downloadUrl: string;
  file: any;
}

@Component({
  selector: 'dspx-document-list',
  templateUrl: './document-list.html',
  styleUrls: ['./document-list.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VptasDocumentListFieldType  extends FieldArrayType implements OnInit {
  dialogClass = 'dialog';
  private formIdentifier: string;
  private activeDialog: string;
  private fileToDelete: any;

  //This property is required to trigger Change Detection on the Primeng Data View.
  public modelForDataview: any;

  public newlyAddedFiles: { name: string, id: string }[] = [];
  emptyMessageText = 'No documents found.';
  acceptedFileTypes = '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,image/*,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
  invalidFileTypeMessageDetail = 'Supported file types are MS Word Documents, MS Excel Workbooks, MS PowerPoint Presentations, PDFs, and Images.';
  fileLimit: number = null;
  maxFileSize: number;
  maxVideoFileSize: number;
  invalidFileSizeMessageDetail: string;
  invalidFileSizeMessageSummary: string;
  compressImages: boolean;
  auto: boolean;
  showDownloadButton = true;
  showRemoveButton = true;
  showViewButton = false;
  onlyNewFilesCanBeRemoved = false;
  useImageAsThumbnail = false;
  fieldKey: string;
  titlePlaceholder = 'Please enter a title';
  enableTitle = false;
  titleRequired = false;

  titleForm = new FormGroup({});
  title: any = {};
  titleOptions: FormlyFormOptions = {};
  titleFormFields: FormlyFieldConfig[] = [];
  documentNumberFieldKey: string;
  documentDateFieldKey: string;
  documentAmountFieldKey: string;
  documentTypeOptions: { label: string, value: string }[] = [];
  uploadText: 'Upload transport document here';
  dropFileText: 'Drop or select file';

  endpoints = this.fileService.endpoints;
  viewFileEndpoint = "api/Document/View";
  @ViewChild('fileUploader') fileUploader!: FileUpload;

  constructor(
    private coreService: CoreService,
    private fileService: FileService,
    private fileSaverService: FileSaverService,
    private spinnerService: SpinnerService,
    private dialogService: DialogService,
    private cd: ChangeDetectorRef,
    private imageCompress: NgxImageCompressService,
    private appSettings: ApplicationSettings,
    private http: HttpClient,
    private appDataService: AppDataService
  ) {
    super();
  }

  ngOnInit(): void {
    // Initialize field model as empty array if undefined to prevent FileUpload errors
    // Check if field.model exists and initialize the specific field key if needed
    if (this.field.model && !this.field.model[this.field.key as string]) {
      this.field.model[this.field.key as string] = [];
    }
    this.setModelForDataView();
    this.fieldKey = this.field.key.toString();
    this.formIdentifier = this.coreService.fileUploadFormIdentifiers[this.fieldKey];
    this.documentNumberFieldKey = this.fieldKey + '_documentNumber';
    this.documentDateFieldKey = this.fieldKey + '_documentListDate';
    this.documentAmountFieldKey = this.fieldKey + '_documentAmount';
    
    if (this.props.listOptions) {
      this.enableTitle = this.props.listOptions.enableTitle
        ? this.props.listOptions.enableTitle
        : false;

      this.titleRequired = this.props.listOptions.titleRequired
        ? this.props.listOptions.titleRequired
        : false;
    }


    if (this.props.fileUploadOptions) {

      this.uploadText = this.props.fileUploadOptions?.documentListUploadText || 'Upload File';
      this.dropFileText = this.props.fileUploadOptions?.documentListDropFileText || 'Drop or select file';

      this.fileLimit = this.props.fileUploadOptions.fileLimit
        ? this.props.fileUploadOptions.fileLimit
        : undefined;

      this.maxFileSize = this.props.fileUploadOptions.maxFileSize
        ? this.props.fileUploadOptions.maxFileSize
        : 10485760;

      this.maxVideoFileSize = this.props.fileUploadOptions.maxVideoFileSize
        ? this.props.fileUploadOptions.maxVideoFileSize
        : 52428800; //50 mb

      this.invalidFileSizeMessageDetail = this.props.fileUploadOptions.invalidFileSizeMessageDetail ? this.props.fileUploadOptions.invalidFileSizeMessageDetail.replace('{maxFileSize}', '{0}') : 'maximum upload size is {0}.'

      this.invalidFileSizeMessageSummary = this.props.fileUploadOptions.invalidFileSizeMessageSummary ? this.props.fileUploadOptions.invalidFileSizeMessageSummary.replace('{fileName}', '{0}') : '{0}: Invalid file size, '

      this.compressImages = this.props.fileUploadOptions.compressImages != null
        ? this.props.fileUploadOptions.compressImages
        : false;

      this.auto = !!this.props.fileUploadOptions.auto;

      this.acceptedFileTypes = this.props.fileUploadOptions.acceptedFileTypes
        ? this.props.fileUploadOptions.acceptedFileTypes
        : this.acceptedFileTypes;

      this.invalidFileTypeMessageDetail = this.props.fileUploadOptions.invalidFileTypeMessage
        ? this.props.fileUploadOptions.invalidFileTypeMessage
        : this.invalidFileTypeMessageDetail;

      this.showDownloadButton = this.props.fileUploadOptions.showDownloadButton != null
        ? this.props.fileUploadOptions.showDownloadButton
        : this.showDownloadButton;

      this.showViewButton = this.props.fileUploadOptions.showViewButton != null
        ? this.props.fileUploadOptions.showViewButton
        : this.showViewButton;

      this.showRemoveButton = this.props.fileUploadOptions.showRemoveButton != null
        ? this.props.fileUploadOptions.showRemoveButton
        : this.showRemoveButton;

      this.useImageAsThumbnail = this.props.listOptions.useImageAsThumbnail != null
        ? this.props.listOptions.useImageAsThumbnail
        : this.useImageAsThumbnail;

      this.onlyNewFilesCanBeRemoved = this.props.fileUploadOptions.onlyNewFilesCanBeRemoved != null
        ? this.props.fileUploadOptions.onlyNewFilesCanBeRemoved
        : this.onlyNewFilesCanBeRemoved;
    }

    const formFields = [
    {
      key: this.documentNumberFieldKey,
      type: FIELD_TYPE.input,
      props: {
        label: 'Invoice number',
        placeholder: 'Invoice number',
        description: 'Invoice number',
        required: false
      }
    },
    {
      key: this.documentDateFieldKey,
      type: FIELD_TYPE.date,
      props: {
        label: 'Document date',
        placeholder: 'Document date',
        description: 'Document date',
        required: false
      }
    },
    {
      key: this.documentAmountFieldKey,
      type: FIELD_TYPE.input,
      props: {
        label: 'Invoice amount',
        placeholder: 'Invoice amount',
        description: 'Invoice amount',
        required: true,
        type: 'number',
        min: 0,
        max: 999999999
      },
      validation: {
        messages: {
          required: 'Please enter an amount',
          min: 'Numeric value required',
          'min-summary': 'Numeric value required',
          max: 'Numeric value required',
          'max-summary': 'Numeric value required',
          'required-summary': 'Required',
        }
      }
    }];
    this.titleFormFields = [...formFields];
  }


  getDocumentIcon(file: any): string {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'pi pi-image';
      case 'doc':
      case 'docx':
        return 'pi pi-file-word';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'pi pi-file-excel';
      case 'pdf':
        return 'pi pi-file-pdf';
      default:
        return 'pi pi-file';
    }
  }

  validateFileSize(file: File, meta?: any): any {

    const validationResult = {
      isValid: true,
      maxFileSize: this.maxFileSize
    } as any;

    const maxFileSize = this.isVideoFile(file) ? this.maxVideoFileSize : this.maxFileSize;
    validationResult.maxFileSize = maxFileSize;

    const skipValidation = this.isImageFile(file) && this.compressImages && !meta;
    if (file.size > maxFileSize && !skipValidation) {
      validationResult.isValid = false;
    }

    return validationResult;
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  downloadButtonClicked(file: any): void {
    if (file != null && file.canDownload) {
      // Use formIdentifier from file object if available, otherwise fall back to component's formIdentifier
      const formIdentifier = file.formIdentifier || this.formIdentifier;
      this.fileService.download(formIdentifier, file.id)
        .pipe(take(1))
        .subscribe({
          next: (response: any) => {
            const dispositionHeader = response.headers.get('content-disposition');
            if (!dispositionHeader) {
              throw new Error('content-disposition header is not found');
            }
            const fileName = file.name != null
              ? file.name
              : this.getContentFileName(dispositionHeader);
            this.fileSaverService.save(response.body, fileName);
          },
          error: (error: any) => {
            console.log('File download error: ', error);
          },
          complete: () => {
            console.log('File download completed');
          },
        });
    }
  }

  viewButtonClicked(file: any): void {
  if (!file || !file.name && (!file.canDownload || !file.canView)) {
    return;
  }

  const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', , 'bmp'];

  const extension = this.getFileExtension(file.name);
  const isViewable = viewableExtensions.includes(extension);

    if (!isViewable) {
      this.dialogService.showSimplePopup("This file type cannot be viewed online. Please download the file to view it.", "File Not Viewable", "OK");
      return;
    }

    // Open new tab synchronously to avoid popup blocking
    const newTab = window.open('', '_blank');

    if (!newTab) {
      console.error('Browser blocked opening a new tab');
      return;
    }

    const formIdentifier = file.formIdentifier || this.formIdentifier;

    const endpointWithParams = `${this.viewFileEndpoint}/${formIdentifier}/${file.id}/${file.name}`;

   this.appDataService.getBlob(endpointWithParams)
    .pipe(take(1))
    .subscribe({
      next: (response) => {
        const blob = response.body;

        if (!blob) {
          newTab.close();
          return;
        }

        const blobUrl = URL.createObjectURL(blob);
        newTab.location.href = blobUrl;

        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
      },
      error: error => {
        newTab.close();
        console.error('File view error:', error);
      }
    });
  }

  removeButtonClicked(file: any): void {
    if (file != null && this.field.model != null) {
      if (!!file.id) {
        this.fileToDelete = file;
        this.activeDialog = 'confirmDocumentDeletion';
        this.dialogService
          .showConfirm('confirmDocumentDeletion')
          .pipe(take(1))
          .subscribe(data => {
            switch (data as DialogResult) {
              case DialogResult.ACCEPTED: {
                console.log('DialogResult.ACCEPTED');
                this.spinnerService.showSpinner();
                this.deleteDialogSubmitted(file);
                break;
              }
              case DialogResult.REJECTED: {
                console.log('DialogResult.REJECTED');
                break;
              }
            }
          });
      }
    }
  }

  isReadonly(): boolean {
    return (this.props.access && this.props.access === 'R');
  }
  
  isActionButtonVisible(file: any, actionButton: any): boolean {
    return file.actions
      && file.actions[actionButton.type] !== null
      && file.actions[actionButton.type] !== undefined
      && file.actions[actionButton.type] === true;
  }

  getActionButtonTitle(label: string, file: any): string {
    return label + ' ' + file.name;
  }

  actionButtonClicked(buttonType: ButtonType, file: any): void {
    const actionButtonEvent = {
      type: buttonType,
      data: file
    };

    if (this.props.actionButtonClick) {
      this.props.actionButtonClick(this.field, actionButtonEvent);
    }
  }

  uploadFiles($event: any): void {
    // Check if adding these files would exceed the limit
    if (this.fileLimit && ($event?.files?.length + (this.model?.length || 0) > this.fileLimit)) {
      return;
    }
    
    // Check for duplicate file names - prevent uploading files that already exist
    if ($event?.files != null && this.model) {
      const duplicateFiles: string[] = [];
      for (const file of $event.files) {
        const existingFile = this.model.find(m => m.name?.toLowerCase() === file.name?.toLowerCase());
        if (existingFile) {
          duplicateFiles.push(file.name);
        }
      }
      
      if (duplicateFiles.length > 0) {
        const fileNames = duplicateFiles.join(', ');
        this.dialogService.showSimplePopup(
          `The following file(s) already exist: ${fileNames}. Please rename the file(s) or remove the existing document before uploading.`,
          'Duplicate File(s) Detected',
          'OK'
        );
        this.fileUploader?.clear(); // Clear the file uploader
        return; // Do not proceed with upload
      }
    }
    
    const dateCtrl = this.titleFormFields?.find(f => f.key === this.documentDateFieldKey)?.formControl;
    if (dateCtrl && dateCtrl.invalid) {
      markChildControlsAsTouched(dateCtrl);
      return;
    }
    const documentAmount = this.title?.[this.documentAmountFieldKey];
    const isDocumentAmountMissing = documentAmount === null || documentAmount === undefined || documentAmount === '';
    if (this.titleRequired && isDocumentAmountMissing) {
      this.titleFormFields.forEach(field => {
        markChildControlsAsTouched(field.formControl);
      });
    }
    else {
      if ($event?.files != null) {
        const compressImages: boolean = this.field.props.fileUploadOptions?.compressImages ?? false;
        const compressImageRatio: number = this.field.props.fileUploadOptions?.compressImageRatio ?? 100;
        const compressImageJpgQuality: number = this.field.props.fileUploadOptions?.compressImageJpgQuality ?? 50;
        const compressImageMaxWidth: number = this.field.props.fileUploadOptions?.compressImageMaxWidth ?? 0;
        const compressImageMaxHeight: number = this.field.props.fileUploadOptions?.compressImageMaxHeight ?? 0;

        for (const file of $event.files) {
          this.spinnerService.showSpinner();
          if ((file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/bmp')
            && compressImages) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
              const originalDataUrl: DataUrl = reader.result.toString();

              this.imageCompress
                .compressFile(
                  originalDataUrl
                  , DOC_ORIENTATION.Default
                  , compressImageRatio
                  , compressImageJpgQuality
                  , compressImageMaxWidth
                  , compressImageMaxHeight)
                .then(async (compressedDataUrl: DataUrl) => {
                  this.createFileThenUpload(compressedDataUrl, file.name);
                });
            };
          }
          // Not an image file that can be compressed, just upload.
          else {
            this.uploadFile(file);
          }
        }
      }
    }
  }

  public isNewlyAddedFile(fileId: string): boolean {
    return this.newlyAddedFiles.findIndex(x => x.id === fileId) !== -1;
  }

  private getContentFileName(contentDispositionHeader: string): string {
    const parts: string[] = contentDispositionHeader.split(';');
    const filename = parts[1].split('=')[1];
    return filename.replace(/['"]+/g, '');
  }

  private deleteDialogSubmitted(file: any): void {
    switch (this.activeDialog) {
      case 'confirmDocumentDeletion':
        // In view details page, use immediate delete endpoint; in forms, use deferred delete
        const useImmediateDelete = this.props.access === 'R' || this.fileToDelete.canDelete === true;
        
        let deleteObservable: Observable<any>;
        if (useImmediateDelete && this.fileService.endpoints.fileDeleteEndpoint) {
          // Use immediate delete endpoint (api/Document/Remove) for view details page
          let formIdToSend = this.fileToDelete.formIdentifier;
          
          // If file doesn't have formIdentifier, fall back to component's formIdentifier
          if (!formIdToSend) {
            formIdToSend = this.formIdentifier;
          }
          
          // Extract GUID from array if needed (array format: [formIdentifierGuid, entityId])
          if (Array.isArray(formIdToSend)) {
            formIdToSend = formIdToSend[0]; // Use first element (form identifier GUID)
          }
          
          // Construct URL properly (include api/ prefix)
          const baseUrl = this.appSettings.apiUrl.replace(/\/$/, ''); // Remove trailing slash
          const deleteUrl = `${baseUrl}/api/${this.fileService.endpoints.fileDeleteEndpoint}`;
          
          // Document/Remove expects formIdentifier in header and array of file IDs in body
          const headers = { 'formidentifier': formIdToSend };
          const documentIds = [this.fileToDelete.id]; 
          
          deleteObservable = this.http.post(deleteUrl, documentIds, { headers });
        } else {
          let formIdentifier = this.fileToDelete.formIdentifier || this.formIdentifier;
          if (Array.isArray(formIdentifier)) {
            formIdentifier = formIdentifier[0];
          }
          deleteObservable = this.fileService.remove(formIdentifier, this.fileToDelete.id);
        }
        
        deleteObservable
          .pipe(take(1))
          .subscribe({
            next: (response: any) => {
              // Model management - safely handle undefined model
              if (this.model) {
                const indexOfRemovedFile = this.model.findIndex(x => x.id === this.fileToDelete.id);
                this.remove(indexOfRemovedFile);
              }
              this.setModelForDataView();
              this.dialogService.showDialog('documentDeletionSuccess');
              this.cd.detectChanges();
            },
            error: (error: any) => {
              this.spinnerService.hideSpinner();
              console.log('File remove error: ', error);
            },
            complete: () => {
              this.spinnerService.hideSpinner();
            }
          });
        break;
    }
  }

  private async createFileThenUpload(dataUrl: DataUrl, fileName: string): Promise<void> {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], fileName);

    //Validate file size before uploading with compressed size
    //The initial validation done when file selected or dropped is skipped for compressed files    
    //To ensure the compressed file size is within the limit
    const isValidFileSize = this.fileUploader.validate(file, { compress: true });

    if (isValidFileSize) {
      this.uploadFile(file);
    }
    else {
      this.spinnerService.hideSpinner();
      this.cd.detectChanges();
    }
  }

  private uploadFile(file: File): void {
    // Rename only .mov files to .mp4 extension
    // This is because .mov file types are not supported by the preview file <video> tag
    // Changing the file to mp4 allows them to be played correctly in the preview
    let fileToUpload = file;
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    if (fileExtension === 'mov') {
      const newFileName = file.name.substring(0, file.name.lastIndexOf('.')) + '.mp4';
      fileToUpload = new File([file], newFileName, { type: 'video/mp4' });
    }

    this.fileService.upload(this.formIdentifier, fileToUpload, this.title[this.documentNumberFieldKey], this.field.key.toString())
      .pipe(take(1)
        , map(id => {
          return {
            id,
            file: fileToUpload
          };
        }))
      .subscribe({
        next: (fileData: any) => {
          if (fileData != null) {
            const fileUploadOnCompleteCallBackMethod = this.field.props.fileUploadOptions?.fileUploadOnComplete;
            if (fileUploadOnCompleteCallBackMethod != null) {
              const documentMetadata = {
                id: fileData.id,
                documentNumber: this.title[this.documentNumberFieldKey],
                documentAmount: this.title[this.documentAmountFieldKey],
                documentDate: this.title[this.documentDateFieldKey]
              };
              var result = this.coreService.applicationFunctions[fileUploadOnCompleteCallBackMethod](file, fileData.id, documentMetadata);
              if (result instanceof Observable) {
                result.subscribe((data) => {
                  this.updateModelAndResetFrom(fileData);
                });
              } else {
                this.updateModelAndResetFrom(fileData);
              }
            } else {
              this.updateModelAndResetFrom(fileData);
            };
          }
        },
        error: (error: any) => {
          this.spinnerService.hideSpinner();
          console.log(`${file.name} upload error: `, error);
        },
        complete: () => {
          this.spinnerService.hideSpinner();
        }
      });
  }

  private generateFileImageThumbnailUrl(fileName: string, fileId: string): string {
    let fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    if (fileExtension === 'jpg' || fileExtension === 'jpeg'
      || fileExtension === 'png' || fileExtension === 'gif' || fileExtension === 'bmp') {
      return this.createAssetUrl(fileId);
    }
    else {
      return '';
    }
  }

  private createAssetUrl(fileId: string) {
    return this.props.fileUploadOptions?.thumbnailUrlTemplate != null
      ? this.props.fileUploadOptions.thumbnailUrlTemplate
        .replace('{baseUrl}', this.appSettings.apiUrl)
        .replace('{formIdentifier}', this.formIdentifier)
        .replace('{id}', fileId)
        .replaceAll('//', '/')
      : '';
  }

  private updateModelAndResetFrom(fileData: any): void {
    let hasCustomActionButton = false;
    if (this.props.actionButtons) {
      hasCustomActionButton = this.props.actionButtons.findIndex(x => x.type === "custom") > -1;
    }
    this.add(null, {
      name: fileData.file.name,
      documentNumber: this.title[this.documentNumberFieldKey],
      documentAmount: this.title[this.documentAmountFieldKey],
      documentDate: this.title[this.documentDateFieldKey],
      id: fileData.id,
      canDownload: this.showDownloadButton,
      canDelete: this.showRemoveButton,
      canView: this.showViewButton,
      isNewlyUploaded: true,
      thumbnailUrl: this.generateFileImageThumbnailUrl(fileData.file.name, fileData.id),
      actions: {
        custom: hasCustomActionButton
      }
    });
    this.setModelForDataView();

    this.newlyAddedFiles.push({ name: fileData.file.name, id: fileData.id });

    this.resetAllFields();

    this.cd.detectChanges();
  }

  private resetAllFields() {
    this.titleFormFields.forEach(field => {
      field.formControl.reset();
    });
  }

  private setModelForDataView() {
    //Setting this fires change detection
    // Safely handle undefined or null model
    if (Array.isArray(this.model)) {
      this.modelForDataview = [...this.model];
    } else {
      this.modelForDataview = [];
    }
  }

  getFilePreviewUrl(file: any): string {
    return this.useImageAsThumbnail ? file.thumbnailUrl || '' : '';
  }

  isImageOrVideoFile(file: any): boolean {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'mp4', 'webm', 'ogg', 'mov', 'mpeg'].includes(fileExtension);
  }

  viewImageButtonClicked(file: any): void {
    if (!file) {
      this.dialogService.showSimplePopup("File is undefined.", "Error", "OK");
      return;
    }

    // Check if file is an image or video
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension)) {
      if (file.thumbnailUrl) {
        this.dialogService.showSimplePopup(`<img src="${file.thumbnailUrl}" style="max-width: 100%; max-height: 80vh;">`, file.name, "Close");
      }
      else {
          this.viewButtonClicked(file);
        }
    } else if (['mp4', 'webm', 'ogg', 'mov', 'mpeg'].includes(fileExtension)) {
      const videoUrl = this.createAssetUrl(file.id);

      if (videoUrl) {
        const videoHtml = `<video controls style="max-width: 100%; max-height: 80vh;">
          <source src="${videoUrl}" type="video/${fileExtension}">
          Your browser does not support the video tag.
        </video>`;
        this.dialogService.showSimplePopup(videoHtml, file.name, "Close");
      } else {
        this.dialogService.showSimplePopup("Video preview URL cannot be shown.", file.name, "Close");
      }
    } else {
      this.dialogService.showSimplePopup("File preview not supported for this type.", file.name, "Close");
    }
  }

  trackFileList(index: number, file: any): string {
    return file?.id;
  }

  isMpegFile(file: any): boolean {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
    return fileExtension === 'mpeg';
  }

  getDocumentTypeLabel(typeOfDocument: string): string {
    const option = this.documentTypeOptions.find(opt => opt.value === typeOfDocument);
    return option ? option.label : typeOfDocument;
  }

  private getFileExtension(fileName: string): string {
  return fileName
    ?.split('.')
    .pop()
    ?.toLowerCase() ?? '';
}

}
